import { useMemo, useState } from 'react'
import { resolveRecipients, buildTeeSheetBody, buildResultsBody, AUTO_MESSAGES } from '../lib/comms.js'
import { sendComms } from '../lib/commsClient.js'
import { updateTournamentInfo } from '../lib/data.js'
import { teamGross, teamVsPar, fmtVsPar, parsFor } from '../lib/scoring.js'

// Message Center — compose an email/SMS, pick recipients (field / division /
// individual) with a live count, send, and see the delivery log. Blasts prefill
// the body from the tee sheet or results. Reuses the admin card chrome.
export default function MessagesTab({ tournamentId, tournament }) {
  const info = tournament?.info || {}
  const regs = tournament?.registrations || {}
  const [channel, setChannel] = useState('email')
  const [scope, setScope] = useState('field')
  const [division, setDivision] = useState('')
  const [regIds, setRegIds] = useState([])
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null)

  const divisions = useMemo(
    () => [...new Set(Object.values(regs).map((r) => r?.division).filter(Boolean))],
    [regs],
  )
  const filter = { scope, division: division || null, regIds: scope === 'individual' ? regIds : null }
  const count = resolveRecipients(regs, filter, { channel }).length

  const prefillTeeSheet = () => { setSubject(`${info.name || 'Tournament'} — Tee Sheet`); setBody(buildTeeSheetBody(tournament, 1)) }
  const prefillResults = () => {
    const pars = parsFor(tournament)
    const ranked = Object.values(tournament?.flights || {}).map((f) => ({
      flightName: f?.info?.name || 'Flight',
      rows: Object.values(f?.teams || {})
        .map((t) => ({ name: t?.info?.name || '', gross: teamGross(t.scores), vs: teamVsPar(t.scores, pars) }))
        .filter((r) => r.gross > 0)
        .sort((a, b) => a.vs - b.vs)
        .map((r) => ({ name: r.name, value: fmtVsPar(r.vs) })),
    }))
    setSubject(`${info.name || 'Tournament'} — Results`)
    setBody(buildResultsBody(tournament, ranked))
  }

  const send = async () => {
    setBusy(true); setResult(null)
    try { setResult(await sendComms({ tournamentId, channel, subject, body, filter })) }
    catch (e) { setResult({ error: e?.message || 'Send failed.' }) }
    finally { setBusy(false) }
  }

  const toggleAuto = (key, on) => {
    updateTournamentInfo(tournamentId, { comms: { ...(info.comms || {}), auto: { ...(info.comms?.auto || {}), [key]: on } } })
  }

  const log = Object.entries(tournament?.commsLog || {})
    .map(([id, v]) => ({ id, ...v }))
    .sort((a, b) => (b.at || 0) - (a.at || 0))

  return (
    <div className="space-y-4">
      <div className="card space-y-3">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="font-bold text-lg mr-2">Message Center</h3>
          <div className="flex gap-1 p-1 rounded-lg bg-gray-100">
            {['email', 'sms'].map((c) => (
              <button key={c} onClick={() => setChannel(c)}
                className={`px-3 py-1 rounded-md text-sm font-bold uppercase ${channel === c ? 'bg-green-deep text-white' : 'text-gray-700'}`}>
                {c}
              </button>
            ))}
          </div>
          <button className="btn-secondary text-sm" onClick={prefillTeeSheet}>Tee sheet</button>
          <button className="btn-secondary text-sm" onClick={prefillResults}>Results</button>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <select className="input w-auto" value={scope} onChange={(e) => setScope(e.target.value)}>
            <option value="field">Whole field</option>
            {divisions.length ? <option value="division">By division</option> : null}
            <option value="individual">Individual</option>
          </select>
          {scope === 'division' ? (
            <select className="input w-auto" value={division} onChange={(e) => setDivision(e.target.value)}>
              <option value="">Select division</option>
              {divisions.map((d) => <option key={d} value={d}>{d}</option>)}
            </select>
          ) : null}
          <span className="text-sm font-bold text-gray-700">{count} recipient{count === 1 ? '' : 's'}</span>
        </div>

        {scope === 'individual' ? (
          <div className="max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-2">
            {Object.entries(regs).map(([id, r]) => (
              <label key={id} className="flex items-center gap-2 py-0.5 text-sm">
                <input type="checkbox" checked={regIds.includes(id)}
                  onChange={(e) => setRegIds((s) => e.target.checked ? [...s, id] : s.filter((x) => x !== id))} />
                {r.teamName || (r.players || []).join(' / ')}
              </label>
            ))}
          </div>
        ) : null}

        {channel === 'email' ? (
          <input className="input" placeholder="Subject" value={subject} onChange={(e) => setSubject(e.target.value)} />
        ) : null}
        <textarea className="input" rows={6} placeholder="Message" value={body} onChange={(e) => setBody(e.target.value)} />

        <div className="flex items-center gap-3">
          <button className="btn-primary" onClick={send} disabled={busy || !body.trim() || count === 0}>
            {busy ? 'Sending…' : `Send to ${count}`}
          </button>
          {result ? (
            result.error
              ? <span className="text-sm font-bold" style={{ color: '#B91C1C' }}>{result.error}</span>
              : <span className="text-sm font-bold text-green-700">Sent {result.sent} · failed {result.failed} of {result.total}</span>
          ) : null}
        </div>
      </div>

      <div className="card">
        <h3 className="font-bold text-base mb-2">Automatic messages</h3>
        <div className="flex flex-wrap gap-4">
          {AUTO_MESSAGES.map((m) => (
            <label key={m.key} className="flex items-center gap-2 text-sm font-semibold text-gray-700">
              <input type="checkbox" checked={!!info.comms?.auto?.[m.key]} onChange={(e) => toggleAuto(m.key, e.target.checked)} />
              {m.label}
            </label>
          ))}
        </div>
      </div>

      <div className="card">
        <h3 className="font-bold text-base mb-2">Delivery log</h3>
        {log.length === 0 ? (
          <p className="text-sm text-gray-500">No messages sent yet.</p>
        ) : (
          <table className="w-full text-sm">
            <thead className="text-xs uppercase text-gray-500 border-b">
              <tr><th className="text-left py-2">When</th><th className="text-left py-2">Channel</th><th className="text-left py-2">To</th><th className="text-right py-2">Sent</th><th className="text-right py-2">Failed</th></tr>
            </thead>
            <tbody>
              {log.map((l) => (
                <tr key={l.id} className="border-b last:border-0">
                  <td className="py-2">{l.at ? new Date(l.at).toLocaleString() : '—'}</td>
                  <td className="py-2 uppercase">{l.channel}</td>
                  <td className="py-2">{l.scope}{l.division ? ` · ${l.division}` : ''}</td>
                  <td className="py-2 text-right font-bold text-green-700">{l.sent}</td>
                  <td className="py-2 text-right font-bold" style={{ color: l.failed ? '#B91C1C' : '#9CA3AF' }}>{l.failed}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}
